import { React, useContext, useState, useEffect } from "react";
import "./Profile.css";
import linkedin from "../../../Utils/linkedin.png";
import { TableHeaderContext } from "../../../Contexts/TableHeaderContext";
import { useGetRatioHighlights, useLatestFinancialHighlights } from "../../DetailPage/FinancialComponents/ratios";
import OverflowToolTip from "../../shared/OverflowToolTip";
import Highlights from "../../DetailPage/Highlights";
import { UserContext } from "../../../Contexts/UserContext";
import Constants from "../../../Constants";
import HeaderButton from "../../DetailPage/FinancialComponents/HeaderButton";
import { X, PhoneOutgoing } from 'lucide-react';
import { useParams } from "react-router-dom";
import { useManagementPhoneAccess } from "../../../hooks/useManagementPhoneAccess";
import { getCfsManagement } from "../../../Config/config";
import FormMCSite from "./CFSFinancial/FormMCSite";
import MCAHeaderButton from "../../DetailPage/FinancialComponents/MCAHeaderButton";
import { Skeleton, Tooltip, Modal } from "antd";
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import axios from "axios";
import { toast } from "react-toastify";
import useSubmitLog from "../../useSumbitLog";
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend
);

function ProfileHighlight({ profile, financials, menu }) {
  const { cfsProfileKeys } = useContext(TableHeaderContext);
  
  const { isTrialExport, isStudent, isDirectoryUser } = useContext(UserContext);
  const allowedModulesStr = localStorage.getItem(Constants.localStorageKeys.allowedModules);
  const allowedModulesList = JSON.parse(allowedModulesStr) ?? [];
  const [openTab, setOpenTab] = useState(6);
  const [selected, setSelected] = useState(false);
  const [showEmployeeChart, setShowEmployeeChart] = useState(false);
  const [creditLinks, setCreditLinks] = useState({});
  const [creditLinksLoading, setCreditLinksLoading] = useState(false);
  const [BrickworksPopup, setBrickWorksPopup] = useState(false);
  const submitLog = useSubmitLog();

  const { companyId } = useParams();
  const CFS_MGMT = `${getCfsManagement}/${companyId}`;

  // Management list (local copy so it refreshes after a save). Synced from the profile prop.
  const [managementInfo, setManagementInfo] = useState(profile?.management_info || { count: 0, data: [] });
  useEffect(() => {
    setManagementInfo(profile?.management_info || { count: 0, data: [] });
  }, [profile?.management_info]);

  const [showContactPopup, setShowContactPopup] = useState(false);

  // Apollo "Get Management" flow
  const [showApolloModal, setShowApolloModal] = useState(false);
  const [apolloPeople, setApolloPeople] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [apolloLoading, setApolloLoading] = useState(false); // searching
  const [savingMgmt, setSavingMgmt] = useState(false);       // enriching/saving

  // Phone reveal (used inside the contact popup when data exists)
  const {
    showConfirmPopup,
    showPaymentPopup,
    phoneNumbers,
    loadingPhone,
    onGetPhoneNumber,
    confirmPopup,
    paymentPopup,
  } = useManagementPhoneAccess(companyId, "cfs");

  const toggleSelect = (id) =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleGetManagement = async () => {
    setApolloLoading(true);
    // Keep the loader visible for at least 3s, even on instant responses,
    // so the result doesn't flash in abruptly.
    const startedAt = Date.now();
    const holdLoader = () =>
      new Promise((resolve) => setTimeout(resolve, Math.max(0, 3000 - (Date.now() - startedAt))));
    try {
      const res = await axios.post(`${CFS_MGMT}/apollo/search/`);
      await holdLoader();
      if (res.data?.success) {
        const people = res.data?.response?.people || [];

        // Filter out people already saved in the management list.
        // Apollo ids differ from our DB ids, so match on name + title instead.
        // Note: saved records use `designation`, Apollo people use `title`; and Apollo
        // search may return only `first_name` (no full `name`) while we save the full name.
        const norm = (s) => (s || "")
          .toString().trim().toLowerCase()
          .replace(/[.,]/g, "")   // drop punctuation (e.g. "Mr. John" vs "Mr John")
          .replace(/\s+/g, " ");  // collapse internal whitespace
        const firstTok = (s) => norm(s).split(" ")[0] || "";
        const existing = managementInfo?.data || [];
        // Full name → catches exact matches.
        const existingNames = new Set(existing.map(m => norm(m.name)).filter(Boolean));
        // first-name + designation → catches Apollo entries that only expose a first name.
        const existingFirstTitle = new Set(
          existing
            .map(m => `${firstTok(m.name)}|${norm(m.designation)}`)
            .filter(k => k !== "|")
        );
        const newPeople = people.filter(p => {
          const pName = norm(p.name) || norm([p.first_name, p.last_name].filter(Boolean).join(" "));
          const pFirstTitle = `${firstTok(p.name || p.first_name)}|${norm(p.title)}`;
          return !(
            (pName && existingNames.has(pName)) ||
            (firstTok(p.name || p.first_name) && norm(p.title) && existingFirstTitle.has(pFirstTitle))
          );
        });

        if (newPeople.length === 0) {
          toast.info(
            people.length === 0
              ? "No people found for this company."
              : "No new people found — all are already added."
          );
        } else {
          setApolloPeople(newPeople);
          setSelectedIds([]);
          setShowApolloModal(true);
        }
      } else {
        toast.error(res.data?.error || "Failed to fetch details.");
      }
    } catch (e) {
      await holdLoader();
      toast.error(e?.response?.data?.error || "Failed to fetch details.");
    } finally {
      setApolloLoading(false);
    }
  };

  const refreshManagement = async () => {
    try {
      const res = await axios.get(`${CFS_MGMT}/`);
      const list = res.data?.management || [];
      setManagementInfo({ count: list.length, data: list });
    } catch (e) { /* keep existing list on failure */ }
  };

  const handleSaveSelected = async () => {
    if (selectedIds.length === 0) { toast.info("Select at least one person."); return; }
    setSavingMgmt(true);
    try {
      const res = await axios.post(`${CFS_MGMT}/apollo/enrich/`, { ids: selectedIds });
      if (res.data?.success) {
        toast.success(`Saved ${res.data.saved_count} management record(s).`);
        setShowApolloModal(false);
        await refreshManagement();
      } else {
        toast.error(res.data?.error || "Failed to save.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.error || "Failed to save.");
    } finally {
      setSavingMgmt(false);
    }
  };

  const epfo_data = profile?.epfo_data || [];
  
  // Format quarter string from "2025Q1" to "Q1' 25"
  const formatQuarterString = (quarterStr) => {
    if (!quarterStr) return '';
    const [year, quarter] = quarterStr.split('Q');
    return `Q${quarter}' ${year.slice(2)}`;
  };
  
  // Sort epfo_data by year and quarter ascending for chart x-axis
  const sortedEpfoData = epfo_data.slice().sort((a, b) => {
    const [yearA, quarterA] = a.qrtr.split('Q').map(Number);
    const [yearB, quarterB] = b.qrtr.split('Q').map(Number);
    return yearA - yearB || quarterA - quarterB;
  });
  
  // Get latest 5 quarters for the chart
  const latestFiveQuarters = sortedEpfoData.slice(-5);
  
  // Get latest entry for display (descending sort)
  const latestEntry = epfo_data.slice().sort((a, b) => {
    const [yearA, quarterA] = a.qrtr.split('Q').map(Number);
    const [yearB, quarterB] = b.qrtr.split('Q').map(Number);
    return yearB - yearA || quarterB - quarterA;
  })[0];

  // Add Employee Count accessor to cfsProfileKeys at the end
  const extendedProfileKeys = [
    ...cfsProfileKeys,
    { 
      accessor: "employee_count", 
      label: latestEntry?.qrtr ? `Employee Count (${formatQuarterString(latestEntry?.qrtr)})` : "Employee Count"
    },
    {
      accessor: "Credits Link",
      label: "Credits Link"
    }
  ];

  // Chart data for employee count - Bar Chart
  const employeeChartData = {
    labels: latestFiveQuarters.map(e => formatQuarterString(e.qrtr)),
    datasets: [
      {
        label: 'Employee Count',
        data: latestFiveQuarters.map(e => e.employees),
        backgroundColor: '#bc8b20',
        borderColor: '#bc8b20',
        borderWidth: 1,
        barThickness: 25, // Control the width of the bars
        maxBarThickness: 30, // Maximum bar width
        borderRadius: 3, // Slightly rounded corners
      },
    ],
  };

  const employeeChartOptions = {
    responsive: true,
    maintainAspectRatio: false, // Allows custom height control
   
    scales: {
      x: { 
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          font: {
            size: 11
          }
        },
        grid: {
          display: false
        },
        categoryPercentage: 0.8, // Controls spacing between groups of bars
        barPercentage: 0.9, // Controls width of bars relative to category space
      },
      y: { 
        beginAtZero: true,
        ticks: {
          stepSize: Math.ceil(Math.max(...latestFiveQuarters.map(e => e.employees)) / 10) || 1
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)'
        }
      },
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: true,
        displayColors: false,
        yAlign : 'bottom',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#000000',
        bodyColor: '#000000',
        borderColor: '#000000',
        borderWidth: 1,
        callbacks: {
          title: () => {
            return '';
          },
          label: (tooltipItem) => {
            return tooltipItem.parsed.y;
          }
        }
      }
    }
  };

  const creditLinksFetcher = async () => {
    setCreditLinksLoading(true);
    submitLog("CRS");
    
    // Dynamic animated toast message
    const availableAgencies = ["BRICKWORK", "CARE", "CRISIL", "ICRA", "FITCH"];
    let currentIndex = 0;

    const toastId = toast.loading(`Checking the ${availableAgencies[0]} website`);

    // Create interval to animate through agencies
    const animationInterval = setInterval(() => {
      currentIndex = (currentIndex + 1) % availableAgencies.length;
      toast.update(toastId, {
        render: `Checking the ${availableAgencies[currentIndex]} website`,
        type: "default",
        isLoading: true
      });
    }, 4000);

    try {
      const res = await axios.post(
        "https://automations-api1.ventureintelligence.com/credit-ratings",
        {
          company_name: profile.full_name
        }
      );

      if (res.status === 200) {
        clearInterval(animationInterval); // Stop animation
        setCreditLinks(res.data);

        toast.update(toastId, {
          render: "Credit ratings fetched successfully",
          type: "success",
          isLoading: false,
          autoClose: 3000
        });
      }
    } catch (error) {
      clearInterval(animationInterval); // Stop animation on error
      toast.update(toastId, {
        render: "Failed to fetch credit ratings",
        type: "error",
        isLoading: false,
        autoClose: 3000
      });
    } finally {
      setCreditLinksLoading(false);
    }
  };

  const RenderCreditLinks = () => {
    return (
      <div>
        <span className="py-1 text-[10pt] lg:whitespace-nowrap leading-[18pt] lg:text-[13px] text-[#333333] cursor-auto">
          Credit Ratings
        </span>
        <div className="mt-1 mb-2">
          {!creditLinks?.credit_ratings ? (
            creditLinksLoading ? (
              <div className="flex items-center gap-2">
                <svg
                  className="animate-spin h-4 w-4 text-[#bc8b20]"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span className="text-[10pt] leading-[18pt] lg:text-[13px] text-[#bc8b20] font-semibold">
                  Checking availability...
                </span>
              </div>
            ) : (
              <p 
                className="text-[10pt] leading-[18pt] lg:text-[13px] text-[#bc8b20] cursor-pointer font-semibold hover:underline"
                onClick={() => creditLinksFetcher()}
              >
                Check availability
              </p>
            )
          ) : (
            (() => {
              const hasBrickwork = creditLinks.credit_ratings.brickwork && creditLinks.credit_ratings.brickwork.length > 0;
              const hasCare = creditLinks.credit_ratings.care;
              const hasCrisil = creditLinks.credit_ratings.crisil;
              const hasIcra = creditLinks.credit_ratings.icra;
              const hasFitch = creditLinks.credit_ratings.fitch;
              const hasAnyData = hasBrickwork || hasCare || hasCrisil || hasIcra || hasFitch;

              if (!hasAnyData) {
                return (
                  <p className="text-[10pt] leading-[18pt] lg:text-[13px] text-[#666666] font-semibold">
                    No data found
                  </p>
                );
              }

              return (
                <div className="flex items-center gap-1">
                  {(() => {
                    const validEntries = Object.entries(creditLinks.credit_ratings)
                      .filter(([key, value]) => {
                        // Filter out entries that don't have valid data
                        if (key === 'brickwork') {
                          return value && Array.isArray(value) && value.length > 0;
                        }
                        return value && value !== '';
                      });

                    return validEntries.map(([key, value], i) => (
                      <span key={key} className="inline-flex items-center">
                        {key === 'brickwork' ? (
                          <span
                            className="text-[10pt] leading-[18pt] lg:text-[13px] text-[#bc8b20] cursor-pointer font-semibold hover:underline"
                            onClick={() => setBrickWorksPopup(true)}
                          >
                            {key.toUpperCase()}
                          </span>
                        ) : (
                          <a
                            href={value}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[10pt] leading-[18pt] lg:text-[13px] text-[#bc8b20] cursor-pointer font-semibold hover:underline"
                          >
                            {key.toUpperCase()}
                          </a>
                        )}
                        {i < validEntries.length - 1 && <span className="text-[#bc8b20]">,</span>}
                      </span>
                    ));
                  })()}
                </div>
              );
            })()
          )}
        </div>
      </div>
    )
  };

  const Brickworks = () => {
    return (
      <div className="fixed inset-0 z-50">
        {/* Backdrop with blur effect */}
        <div 
          className="fixed inset-0 bg-black/30 backdrop-blur-sm"
          onClick={() => setBrickWorksPopup(false)}
        />
        
        {/* Modal container */}
        <div className="fixed inset-0 flex items-center justify-center p-4">
          {/* Modal content */}
          <div className="relative bg-white rounded-lg overflow-hidden max-w-2xl w-full max-h-[80vh]">
            {/* Close button */}
            <div className="absolute top-0 right-0 p-4 z-10">
              <button
                onClick={() => setBrickWorksPopup(false)}
                className="rounded-full p-1 hover:bg-gray-100 transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            
            {/* Header */}
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Brickwork Credit Ratings</h2>
            </div>
            
            {/* Content */}
            <div className="px-6 py-4 overflow-y-auto max-h-[60vh]">
              {creditLinks.credit_ratings.brickwork?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-gray-700 bg-gray-50">Date</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700 bg-gray-50">Title</th>
                      </tr>
                    </thead>
                    <tbody>
                      {creditLinks.credit_ratings.brickwork.map((rating, index) => (
                        <tr key={`brickwork-${index}`} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">
                            {rating.date}
                          </td>
                          <td className="py-3 px-4">
                            <a
                              href={rating.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#bc8b20] hover:text-[#9a7419] font-medium hover:underline text-sm"
                            >
                              {rating.title}
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No Brickwork ratings available</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Modal */}
      {selected && (
        <div className="fixed inset-0 z-10 ">
          {/* Backdrop with blur effect */}
          <div 
            className="fixed inset-0 bg-black/30 backdrop-blur-sm"
            onClick={() => setSelected(false)}
          />
          
          {/* Modal container with padding for scrolling */}
          <div className="fixed inset-0 flex items-center justify-center p-4">
            {/* Modal content */}
            <div className="relative bg-white rounded-lg overflow-hidden">
              {/* Close button */}
              <div className="absolute top-0 right-0 p-4">
                <button
                  onClick={() => setSelected(false)}
                  className="rounded-full p-1 hover:bg-gray-100 transition-colors"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
              
              {/* Form content with scrollable area */}
              <div className="overflow-y-auto max-h-[85vh] mt-6 mr-4">
                <FormMCSite tab={1} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="lg:mt-4 px-4 py-4 w-[100%]">
        <div className="lg:bg-white rounded-[6px]" x-show="tab === 1">
          <div className="w-full lg:grid lg:grid-cols-5 rounded-[6px] shadow-[0_2px_6px_0_rgba(0,0,0,0.08)]">
            <div className="mt-3 lg:mt-0 rounded-lg lg:rounded lg:col-span-3 lg:border-r bg-white">
              <div className="mx-5 py-4">
                <div className="flex justify-between items-center">
                  <p className="text-[14pt] flex leading-[19pt] lg:text-[17px] text-[#333333] font-semibold lg:leading-[22px]">
                    Profile
                    {profile?.linkedin && (
                      <a
                        href={profile.linkedin}
                        target="_blank"
                        className="mt-[0.20rem] ml-2"
                        rel="noopener noreferrer"
                      >
                        <img src={linkedin} width={16} alt="linkedin logo" />
                      </a>
                    )}
                  </p>
                  {/* <MCAHeaderButton
                    isSelected={!selected}
                    onClick={() => setSelected(!selected)}
                  >
                    From MCA Site
                  </MCAHeaderButton> */}
                </div>

                <div className="mt-2">
                  {profile?.length === 0 ? (
                    <div className="overflow-x-auto lg:overflow-x-hidden overflow-y-hidden lg:overflow-y-hidden">
                      <table className="h-[10rem] w-full bg-[#FFFFFF]">
                        <center>
                          <div role="status" className="bg-white h-[16rem]">
                            <svg
                              className="relative top-[6rem] inline mr-2 w-12 h-12 text-gray-200 animate-spin dark:text-gray-600 fill-[#CA8A24]"
                              viewBox="0 0 100 101"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C0 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                fill="currentColor"
                              />
                              <path
                                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                fill="currentFill"
                              />
                            </svg>
                          </div>
                        </center>
                      </table>
                    </div>
                  ) : (
                    <div className="overflow-x-auto lg:overflow-x-hidden overflow-y-hidden lg:overflow-y-hidden">
                      <div className="w-full bg-[#FFFFFF]">
                        <div className="grid md:grid-cols-3 grid-cols-2 break-words">
                          {extendedProfileKeys.map((k, index) => {
                            const key = k.accessor;
                            const label = k.label;
                            let valueToRender = null;

                            if (key === "management_info") {
                              // Always show: without a website the backend resolves the company
                              // on Apollo by name (mixed_companies search) before fetching people.
                              return (
                                <div key={index} className="pr-4">
                                  <div className="leading-[21px] font-sans_book_body mt-1">
                                    <span className="py-1 text-[10pt] lg:whitespace-nowrap leading-[18pt] lg:text-[13px] text-[#333333]">{label}</span>
                                    <div className="mt-1 mb-2 text-[10pt] leading-[18pt] lg:text-[13px] text-[#333333] font-semibold">
                                      {managementInfo?.count > 0 ? (
                                        <span className="flex flex-wrap items-center">
                                          {managementInfo.count === 1 && managementInfo.data[0]?.email
                                            ? <>
                                              <span className="text-[#bc8b20] cursor-pointer" onClick={() => setShowContactPopup(true)}>{managementInfo.data[0].name}</span>
                                            </> 
                                            : `${managementInfo.data[0]?.name}`}{" "}
                                          {managementInfo.count > 1 && (
                                            <span className="text-[#bc8b20] cursor-pointer" onClick={() => setShowContactPopup(true)}>{` & ${managementInfo.count - 1} more`}</span>
                                          )}
                                        </span>
                                      ) : (
                                        <button
                                          onClick={handleGetManagement}
                                          disabled={apolloLoading}
                                          className="text-[#bc8b20] cursor-pointer font-semibold hover:underline disabled:opacity-60"
                                        >
                                          {apolloLoading ? (
                                            <span className="flex items-center gap-1.5">
                                              <span className="inline-block w-3.5 h-3.5 border-2 border-[#bc8b20] border-t-transparent rounded-full animate-spin" />
                                              Fetching…
                                            </span>
                                          ) : "Fetch Details"}
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            } else if (key === "contact_name") {
                              const contactName = profile["contact_name"];
                              const contactDesignation = profile["contact_designation"];
                              if (contactName && contactDesignation) {
                                valueToRender = `${contactName}, ${contactDesignation}`;
                              } else if (contactName) {
                                valueToRender = contactName;
                              } else if (contactDesignation) {
                                valueToRender = contactDesignation;
                              }
                            } else if (key === "city_data") {
                              const city = profile[key];
                              if (city && city.name) {
                                valueToRender = `${city.name}, ${city.state}, ${city.region}, ${city.country}`;
                              }
                            } else if (key === "employee_count") {
                              // Render Employee Count similar to second file
                              valueToRender = latestEntry && latestEntry.qrtr && latestEntry.employees !== undefined
                                ? (
                                  <Tooltip
                                    title={<span style={{ color: 'black' }}>Click to view employee count chart</span>}
                                    color="white"
                                  >
                                    <span
                                      style={{ cursor: 'pointer', color: '#bc8b20', fontWeight: 600 }}
                                      onClick={() => setShowEmployeeChart(true)}
                                    >
                                      {latestEntry.employees}
                                    </span>
                                  </Tooltip>
                                )
                                : null;
                            } else {
                              valueToRender = profile[key] && profile[key] !== "" ? profile[key] : null;
                            }

                            if (valueToRender !== null) {
                              return (
                                <div key={index} className="pr-4">
                                  {typeof valueToRender === "string" && valueToRender.length > 30 ? (
                                    <div className="leading-[21px] font-sans_book_body mt-1">
                                      <div className="block lg:overflow-x-clip lg:overflow-ellipsis lg:whitespace-nowrap md:max-w-[14rem]">
                                        <span
                                          className="py-1 text-[10pt] lg:whitespace-nowrap leading-[18pt] lg:text-[13px] text-[#333333] cursor-auto"
                                          readOnly
                                        >
                                          {label}
                                        </span>
                                      </div>
                                      <Tooltip
                                        title={
                                          <>
                                            <div className="block lg:overflow-x-clip lg:overflow-ellipsis lg:whitespace-nowrap md:max-w-[14rem]">
                                              <span
                                                className="py-1 text-[10pt] lg:whitespace-nowrap leading-[18pt] lg:text-[13px] text-[#333333] cursor-auto"
                                                readOnly
                                              >
                                                {label}
                                              </span>
                                            </div>
                                            {label === 'Website' ? (
                                              <a
                                                href={valueToRender}
                                                className="mt-1 mb-2 hover:text-[#bc8b20] text-[10pt] leading-[18pt] lg:text-[13px] text-[#333333] cursor-pointer font-semibold"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                              >
                                                {valueToRender}
                                              </a>
                                            ) : (
                                              <p
                                                className="mt-1 mb-2 text-[10pt] leading-[18pt] lg:text-[13px] text-[#333333] cursor-auto font-semibold"
                                                readOnly
                                              >
                                                {valueToRender}
                                              </p>
                                            )}
                                          </>
                                        }
                                        color="white"
                                        placement="topLeft"
                                      >
                                        {label === "Website" ? (
                                          <p
                                            className="w-[90%] overflow-ellipsis overflow-hidden whitespace-nowrap mt-1 mb-2 text-[10pt] leading-[18pt] lg:text-[13px] text-[#333333] cursor-auto font-semibold"
                                            readOnly
                                          >
                                            <a
                                              href={valueToRender}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                            >
                                              {valueToRender}
                                            </a>
                                          </p>
                                        ) : (
                                          <p
                                            className="w-[90%] overflow-ellipsis overflow-hidden whitespace-nowrap mt-1 mb-2 text-[10pt] leading-[18pt] lg:text-[13px] text-[#333333] cursor-auto font-semibold"
                                            readOnly
                                          >
                                            {valueToRender}
                                          </p>
                                        )}
                                      </Tooltip>
                                    </div>
                                  ) : (
                                    <div className="leading-[21px] font-sans_book_body mt-1">
                                      <div className="block lg:overflow-x-clip lg:overflow-ellipsis lg:whitespace-nowrap md:max-w-[14rem]">
                                        <span
                                          className="py-1 text-[10pt] lg:whitespace-nowrap leading-[18pt] lg:text-[13px] text-[#333333] cursor-auto"
                                          readOnly
                                        >
                                          {label}
                                        </span>
                                      </div>
                                      {label === "Website" ? (
                                        <a
                                          href={valueToRender}
                                          className="mt-1 mb-2 text-[10pt] leading-[18pt] lg:text-[13px] text-[#333333] cursor-pointer font-semibold"
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          {valueToRender}
                                        </a>
                                      ) : (
                                        <p
                                          className="mt-1 mb-2 text-[10pt] leading-[18pt] lg:text-[13px] text-[#333333] cursor-auto font-semibold"
                                          readOnly
                                        >
                                          {valueToRender}
                                        </p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            }
                            return null;
                          })}
                          {RenderCreditLinks()}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <Highlights financials={financials} menu={menu} />
          </div>
        </div>
      </div>
      
      {/* Employee Count Bar Chart Modal */}
      <Modal
        open={showEmployeeChart}
        onCancel={() => setShowEmployeeChart(false)}
        footer={null}
        title="Employee Count"
        width={600}
      >
        <div style={{ marginBottom: '10px', textAlign: 'center', color: '#666', paddingTop: '6px' }}>
        </div>
        <div style={{ height: '300px', padding: '10px', position: 'relative' }}>
          <Bar data={employeeChartData} options={employeeChartOptions} />
          <div style={{ 
            position: 'absolute', 
            bottom: '-12px', 
            right: '-11px', 
            fontSize: '11px', 
            color: '#666',
            fontStyle: 'italic'
          }}>
            Source: Labour filings
          </div>
        </div>
      </Modal>

      {BrickworksPopup && <Brickworks />}

      <Modal
        open={showApolloModal}
        onCancel={() => setShowApolloModal(false)}
        title="Select management information to add"
        okText={savingMgmt ? "Saving…" : `Save selected (${selectedIds.length})`}
        onOk={handleSaveSelected}
        okButtonProps={{ disabled: savingMgmt || selectedIds.length === 0, style: { background: '#bc8b20', color: "white" } }}
        confirmLoading={savingMgmt}
        width={640}
        zIndex={10000}
      >
        <div className="max-h-[50vh] overflow-y-auto">
          {apolloPeople.map((p) => (
            <label key={p.id} className="flex items-center gap-3 py-2 border-b cursor-pointer text-[13px]">
              <input type="checkbox" checked={selectedIds.includes(p.id)} onChange={() => toggleSelect(p.id)} />
              <span className="font-semibold">{p.first_name}</span>
              <span className="text-[#666]">{p.title || "—"}</span>
            </label>
          ))}
        </div>
      </Modal>

      {showContactPopup && managementInfo?.count > 0 && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.35)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={() => setShowContactPopup(false)}
        >
          <div
            className="relative bg-white p-8 pt-0 rounded-lg"
            style={{ minWidth: 900, maxWidth: 1500, boxShadow: '0 2px 12px rgba(0,0,0,0.15)' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="py-4 flex justify-end">
              <button
                  onClick={() => setShowContactPopup(false)}
                  className="rounded-full p-1 hover:bg-gray-100 transition-colors"
              >
                  <X className="h-5 w-5 text-gray-500" />
              </button>
          </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: '#ffffffff' }}>
                    <th style={{ padding: '6px 8px', border: '1px solid #ffffffff', textAlign: 'left' }}>Name</th>
                    <th style={{ padding: '6px 8px', border: '1px solid #ffffffff', textAlign: 'left' }}>Designation</th>
                    <th style={{ padding: '6px 8px', border: '1px solid #ffffffff', textAlign: 'left' }}>Email</th>
                    <th style={{ padding: '6px 8px', border: '1px solid #ffffffff', textAlign: 'left' }}>Phone</th>
                    <th style={{ padding: '6px 10px', border: '1px solid #ffffffff', textAlign: 'left' }}>LinkedIn</th>
                  </tr>
                </thead>
                <tbody>
                  {managementInfo?.data?.map((info, idx) => (
                    <tr key={idx}>
                      <td style={{ padding: '6px 8px', border: '1px solid #ffffffff' }}>{info.name || '-'}</td>
                      <td style={{ padding: '6px 8px', border: '1px solid #ffffffff' }}>{info.designation || '-'}</td>
                      <td style={{ padding: '6px 8px', border: '1px solid #ffffffff' }}>
                        {info.email ? <a href={`mailto:${info.email}`}>{info.email}</a> : '-'}
                      </td>
                      <td style={{ padding: '6px 8px', border: '1px solid #ffffffff' }}>
                        {phoneNumbers[idx] || info?.phone ? (
                          <span>{phoneNumbers[idx] || info.phone}</span>
                        ) : (
                          <div
                            onClick={() => onGetPhoneNumber(info, idx)}
                            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                            title="Click to reveal phone number"
                          >
                            {loadingPhone[idx] ? (
                              <span style={{ fontSize: '12px', color: '#9CA3AF' }}>Loading...</span>
                            ) : (
                              <PhoneOutgoing size={16} style={{ color: '#10B981' }} />
                            )}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '6px 20px', border: '1px solid #ffffffff' }}>
                        {info.linkedin_url ? (
                          <a href={info.linkedin_url} target="_blank" rel="noopener noreferrer">
                            <img src={linkedin} width={16} alt="linkedin logo" />
                          </a>
                        ) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                style={{ marginTop: 12, marginRight: 8, background: '#bc8b20', color: 'white', border: 'none', borderRadius: 4, padding: '4px 16px', cursor: apolloLoading ? 'not-allowed' : 'pointer', fontSize: 13, opacity: apolloLoading ? 0.6 : 1 }}
                onClick={handleGetManagement}
                disabled={apolloLoading}
              >
                {apolloLoading ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span className="inline-block w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Checking…
                  </span>
                ) : 'Fetch More'}
              </button>
              <button
                style={{ marginTop: 12, background: '#a07924', color: 'white', border: 'none', borderRadius: 4, padding: '4px 16px', cursor: 'pointer', fontSize: 13 }}
                onClick={() => setShowContactPopup(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {showConfirmPopup && confirmPopup()}
      {showPaymentPopup && paymentPopup()}
    </>
  );
}

export default ProfileHighlight;